import React, {useEffect, useState} from 'react';
import cx from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import {useNavigate} from 'react-router-dom';
import './PrequalReview.scss';
import '../FormHeaderSection/FormHeaderSection.scss';
import {
  updateAcceptedTermsAndConditions,
  updateCurrentModalPage,
  updateHighestPageHit,
  updateResult,
  updateShowCancelPreQualModal
} from '../../../redux/app-state-slice';
import {checkIfReviewPageIsValid} from '../../../validation/FormValidation';
import {tagReviewFormSubmitButtonClick} from '../../../Services/TaggingService/TaggingService';
import {trackEvents} from '../../../Services/OptimizelyService/OptimizelyService';
import {isOutOfOrder} from '../../../utils/functions/Functions';
import {submitApplication} from '../../../Services/SubmitApplicationService/SubmitApplicationService';
import {getQueryElement} from '../../../utils/queryElement';
import {logService} from '../../../Services/LogService/LogService';
import {FormHeaderSection} from '../FormHeaderSection/FormHeaderSection';
import ModalDialog from '../../../components/ModalDialog/ModalDialog';
import {EditPersonalInfoButton} from '../../../components/EditButtons/EditPersonalInfoButton';
import PersonalInfoReview from './PersonalInfoReview/PersonalInfoReview';
import {EditResidenceButton} from '../../../components/EditButtons/EditResidenceButton';
import ResidenceInfoReview from './ResidenceInfoReview/ResidenceInfoReview';
import {EditIncomeButton} from '../../../components/EditButtons/EditIncomeButton';
import IncomeInfoReview from './IncomeInfoReview/IncomeInfoReview';
import Checkbox from '../../../components/Checkbox/Checkbox';
import errorIcon from '../../../assets/icons/error.svg';
import TimeOutSession from '../../../components/TimeOutSession/TimeOutSession';
import {SPECIAL_EVENT_PAGE_NAME} from '../../../utils/constants/constants';
import FooterNavigation, {closeEvent} from '../../../components/FooterNavigation/FooterNavigation';
import {closeModal} from '../../../utils/tier-3-modal-close/PrequalCloseEvent';

export const onSubmitCallback = (result, navigate, customerData, dispatch) => {
  const isDealerModal = customerData.showModalFlag.value && customerData.isDealerHostApplication.value;

  if (result?.message === 'Maintenance') {
    dispatch(updateCurrentModalPage('/error'));
  } else if (result.decisionCode === 'APPROVED' || result.decisionCode === 'NOT_APPROVED') {
    dispatch(updateResult(result));
    isDealerModal ? dispatch(updateCurrentModalPage('/review-offer')) : navigate('/review-offer', {state: {result}});
  } else {
    dispatch(updateCurrentModalPage('/error'));
  }
};

function postSubmission(submitApplicationData, customerData, dispatch, specialOffers, standardOffers, navigate) {
  let result = {...submitApplicationData};
  customerData.showModalFlag.value ? dispatch(updateHighestPageHit(2)) : dispatch(updateHighestPageHit(customerData));

  if (specialOffers && standardOffers) {
    result = {...result, specialOffers, standardOffers};
  }
  onSubmitCallback(result, navigate, customerData, dispatch);
}

function postSubmissionError(customerData, dispatch) {
  if (!customerData.showModalFlag.value) {
    dispatch(updateHighestPageHit(customerData));
  }
  dispatch(updateCurrentModalPage('/error'));
}

export const onSubmit = (reCaptchaToken, customerData, appConfig, navigate, dispatch) => {
  if (checkIfReviewPageIsValid(customerData)) {
    customerData.showModalFlag.value ? dispatch(updateCurrentModalPage('/offer-loading')) : navigate('/offer-loading');
    const submitApplicationResponse = [submitApplication(reCaptchaToken, customerData, appConfig)];

    Promise.all(submitApplicationResponse)
      .then(([submitApplicationData, specialOffers, standardOffers]) => {
        postSubmission(submitApplicationData, customerData, dispatch, specialOffers, standardOffers, navigate);
      })
      .catch(() => {
        postSubmissionError(customerData, dispatch);
      });
  }
};

export const isScrolledToBottom = (scrollableElement) => {
  const {scrollHeight, scrollTop, clientHeight} = scrollableElement;
  return Math.abs(scrollHeight - (clientHeight + scrollTop)) < 5;
};

function PrequalReview({customerData, appConfig, dispatch}) {
  const navigate = useNavigate();
  const mobile = window.screen.width <= 1023;
  const [didScrollToBottomOfTerms, setDidScrollToBottomOfTerms] = useState(false);
  const recaptchaRef = React.createRef();
  const getTextBasedOnDevice = (mobileText, desktopText) => (mobile ? mobileText : desktopText);

  const onChange = (value) => {
    onSubmitHandler(value);
  };
  const onCancelPrequalAppHandler = () => {
    closeModal(dispatch, customerData);
  };

  const onClose = () => {
    dispatch(updateShowCancelPreQualModal(false));
  };

  const onAcceptTermsHandler = () => {
    dispatch(updateAcceptedTermsAndConditions(!customerData.acceptedTermsAndConditions.value));
  };

  const onSubmitHandler = (reCaptchaToken) => {
    tagReviewFormSubmitButtonClick(customerData.optimizelyExperimentData, customerData.specialEvent.value);
    setTimeout(() => {
      onSubmit(reCaptchaToken, customerData, appConfig, navigate, dispatch);
    }, 100);
  };

  const onClick = () => {
    dispatch(updateAcceptedTermsAndConditions(didScrollToBottomOfTerms && !customerData.acceptedTermsAndConditions.value));
  };

  const loadRecaptcha = async () => {
    if (appConfig.reCaptchaFlag && customerData.prequalSource.value === 'web') {
      await recaptchaRef.current.executeAsync(); // invoke the challenge and return a promise that resolves to the token or errors(if encountered).
    }
  };

  const onSubmitButtonClick = async () => {
    if (customerData?.recaptchaOptimizelyData?.isEnabled) {
      trackEvents('submitButtonEvent', customerData);
    }
    if (
      customerData?.recaptchaOptimizelyData?.variationKey === 'treatment' &&
      appConfig.reCaptchaFlag &&
      customerData.prequalSource.value === 'web'
    ) {
      await loadRecaptcha();
    } else {
      onSubmitHandler('');
    }
  };

  function logPrequalReviewPage() {
    let logInfo = {
      logType: 'TIER1_NAVIGATING_TO_REVIEW_PAGE',
      countryCode: customerData.countryCode.value
    };
    if (customerData.isDealerHostApplication.value) {
      logInfo = {
        logType: 'TIER3_NAVIGATING_TO_REVIEW_PAGE',
        paCode: customerData.tier3PACode.value,
        prequalSource: customerData.prequalSource.value,
        vin: customerData.tier3Vin.value
      };
    }
    logService(logInfo, appConfig)
      .then(() => {})
      .catch(() => {});
  }

  function validateReviewPage() {
    return process.env.NODE_ENV === 'test' ? false : checkIfReviewPageIsValid(customerData);
  }

  function closeModalAndCallOptimizelyTrackEvent() {
    if (customerData?.recaptchaOptimizelyData?.isEnabled) {
      trackEvents('pq_review_bounce', customerData);
    }
    closeEvent(customerData, dispatch);
  }

  const props = {};
  props.primaryButtonLabel = 'Submit';
  props.primaryButtonPath = '/review-offer';
  props.primaryButtonTestId = 'submit-application';
  props.primaryButtonOnClickEvent = appConfig.reCaptchaFlag ? onSubmitButtonClick : () => onSubmitHandler('');
  props.closeButtonOnClickEvent = closeModalAndCallOptimizelyTrackEvent;
  props.buttonIsDisabled = validateReviewPage();

  function getReCAPTCHA() {
    if (customerData?.recaptchaOptimizelyData?.isEnabled && appConfig.reCaptchaFlag && customerData.prequalSource.value === 'web') {
      return <ReCAPTCHA sitekey={customerData?.config?.recaptchaSiteKey} onChange={onChange} size='invisible' ref={recaptchaRef} />;
    }
    return '';
  }

  useEffect(() => {
    const queryElement = getQueryElement(customerData);
    queryElement.getElementById('prequal--web--form-section')?.scrollIntoView();
    queryElement.getElementById('terms-and-conditions-container').addEventListener('scroll', (event) => {
      setDidScrollToBottomOfTerms((state) => state || isScrolledToBottom(event.target));
    });

    dispatch(updateAcceptedTermsAndConditions(false));
    if (!customerData?.isDealerHostApplication?.value && isOutOfOrder(customerData)) {
      customerData.specialEvent.value ? navigate(`/${SPECIAL_EVENT_PAGE_NAME}`) : navigate('/');
    }
    dispatch(updateHighestPageHit(2));
    logPrequalReviewPage();
    trackEvents('reviewPageView', customerData);
  }, []);

  return (
    <>
      {getReCAPTCHA()}
      <div id='prequal--web--form-section' className='prequal--wc--form-section' data-testid='prequal--web--form-section'>
        <FormHeaderSection
          isDealerHostApplication
          dispatch={dispatch}
          customerData={customerData}
          navigate={navigate}
          headerText='Review and submit your info.'
        />
        <div
          className={`${customerData.isDealerHostApplication.value ? '' : 'prequal--web--form-section-container'} prequal--wc--form-section-container`}
          data-testid='form-section-container'
          id='review-form-section-container'
        >
          <ModalDialog
            showDialog={customerData.showCancelPreQualModal.value}
            onClose={onClose}
            onClickSecondary={onCancelPrequalAppHandler}
            heading={getTextBasedOnDevice('Cancel Prequalification?', 'Exit Prequalification')}
            dialogContent={
              <div className='prequal--web--cancel-prequalification-text-container'>
                <p>Are you sure you want to cancel your prequalification process? Please note that your information will not be saved.</p>
              </div>
            }
            hasCloseIcon
            hasCloseButton
            closeButtonText={getTextBasedOnDevice('Resume Prequalification', 'Yes, Resume Prequalification')}
            hasSecondaryButton
            secondaryButtonText={getTextBasedOnDevice('Yes, Cancel', 'Exit Prequalification')}
            modalType='CancelPrequalification'
          />
          <div data-testid='form-section-header-dealer-host' className='prequal--wc--form-section-header-dealer-host-fomo'>
            <h1>Review Your Info</h1>
          </div>
          <div className='prequal--web--review-and-submit-personal-container'>
            <div className={cx('prequal--web--review-section-title-padding-fomo prequal--web--bottom-border')}>
              <h2
                className={cx('prequal--web--header-fomo', {
                  'prequal--wc--personal-info-header': customerData.isDealerHostApplication.value
                })}
              >
                Personal Information
              </h2>
              {!customerData.cardEditFlags.personalInfoEditMode.value && (
                <EditPersonalInfoButton dispatch={dispatch} customerData={customerData} />
              )}
            </div>
            <PersonalInfoReview customerData={customerData} dispatch={dispatch} appConfig={appConfig} />
          </div>
          <div className='prequal--web--review-and-submit-residence-container'>
            <div className={cx('prequal--web--bottom-border', 'prequal--web--review-section-title-padding-fomo')}>
              <h2 className='prequal--web--header-fomo'>Residence Information</h2>
              {!customerData.cardEditFlags.residenceEditMode.value && (
                <EditResidenceButton dispatch={dispatch} customerData={customerData} />
              )}
            </div>
            <ResidenceInfoReview customerData={customerData} dispatch={dispatch} appConfig={appConfig} />
          </div>
          <div className='prequal--web--review-and-submit-income-container'>
            <div className={cx('prequal--web--bottom-border', 'prequal--web--review-section-title-padding-fomo')}>
              <h2 className='prequal--web--header-fomo'>Income Information</h2>
              {!customerData.cardEditFlags.incomeEditMode.value && <EditIncomeButton dispatch={dispatch} customerData={customerData} />}
            </div>
            <IncomeInfoReview customerData={customerData} dispatch={dispatch} appConfig={appConfig} />
          </div>
          <div className='prequal--web--review-and-submit-footer-fomo'>
            <div
              className={cx(
                {'prequal--web--review-page-checkbox-container-fomo': !customerData.isDealerHostApplication.value},
                {
                  'prequal--wc--review-page-checkbox-container': customerData.isDealerHostApplication.value
                }
              )}
            >
              <div className='prequal--web--review-and-submit-terms-container-fomo'>
                <div
                  id='terms-and-conditions-container'
                  data-testid='terms-and-conditions-container'
                  className='prequal--web--review-and-submit-terms-fomo'
                >
                  <h3 className='prequal--web--review-and-submit-terms-title-fomo'>Pre-Qualification Terms &amp; Conditions</h3>
                  <br />
                  {customerData.countryCode.value === 'CAN' ? (
                    <p className='prequal--web--review-and-submit-terms-p-fomo'>
                      To prequalify from us, you certify that the information in your application is accurate and complete to the best of
                      your knowledge and that you have attained the age of majority in your province or territory of residence.
                      <br />
                      You authorize Ford Credit Canada Company (Ford Credit) or its affiliated entities that provide automotive financial
                      services (“Creditor” or “we” or “us” or “our”) to obtain your credit report and understand that a credit report will
                      be requested in connection with this application for prequalification.
                      <br />
                      If prequalified, the amount will be an estimate determined by the information you submitted, along with the personal
                      credit profile from credit reporting agencies obtained by Ford Credit through your prequalification request. This
                      amount may change if you update any information on the Ford Credit prequalification website or at the Ford dealer.
                      After selecting a vehicle and submitting your full credit application through the dealership, Ford Credit will review
                      your creditworthiness. Prequalification does not guarantee approval for financing or particular terms, it is a tool to
                      assist your shopping experience. Your terms are only final once your financing is completed at a participating dealer.
                      <br />
                      You understand that Ford Credit financing is available only through participating dealers. The finance or lease
                      transaction is completed with the dealer. Your prequalification will be with Ford Credit. You also expressly consent
                      and agree to us, our affiliates, agents and service providers using written, electronic or verbal means to contact you
                      in response to this request. This consent includes, but is not limited to, contact by manual calling methods,
                      prerecorded or artificial voice messages, text messages, emails and/or automatic telephone dialing systems. You agree
                      that we, our affiliates, agents and service providers may do so using any e-mail address or any telephone number you
                      provide, now or in the future, including a number for a cellular phone or other wireless device, regardless of whether
                      you incur charges as a result. You consent to receive the information, follow-up communications, and disclosures
                      related to this application and any subsequent credit transaction from Ford Credit affiliates, agents, and service
                      providers, in writing, verbally or electronically. You have reviewed the Ford Credit{' '}
                      <a href='https://www.ford.ca/finance/privacy' target='_blank' rel='noreferrer'>
                        privacy statement
                      </a>{' '}
                      and the privacy policy for the dealer you select, if applicable.
                      {customerData?.stateOrProvince?.value === 'QC' && (
                        <span>
                          <br />
                          <br />
                          The decision will be based on the automated processing of your Information. Should you wish to inquire about an
                          automated decision you may contact our Customer Service Centre at Box 1800 RPO Lakeshore West, Oakville, Ontario,
                          L6K 0J8. Attention: Privacy Officer
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className='prequal--web--review-and-submit-terms-p-fomo'>
                      For the purpose of pre-qualifying from us, you certify that the information in your application is true and complete
                      to the best of your knowledge, and you certify that you have attained the age of majority.
                      <br />
                      <br />
                      You authorize Ford Motor Credit Company LLC (Ford Credit) or its affiliated entities that provide automotive financial
                      services (“Creditor” or “we” or “us” or “our”) to obtain your credit report and understand that a credit report will
                      be requested in connection with this application for prequalification.
                      <br />
                      <br />
                      You understand that Ford Credit financing is available only through participating dealers and authorize Ford Credit to
                      share data obtained in the prequalification process with your selected dealer. The finance or lease transaction is
                      completed with the dealer. Your prequalification will be with Ford Credit.
                      <br />
                      <br />
                      You also expressly consent and agree to us, our affiliates, agents and service providers using written, electronic or
                      verbal means to contact you. This consent includes, but is not limited to, contact by manual calling methods,
                      prerecorded or artificial voice messages, text messages, emails and/or automatic telephone dialing systems. You agree
                      that we, our affiliates, agents and service providers may do so using any e-mail address or any telephone number you
                      provide, now or in the future, including a number for a cellular phone or other wireless device, regardless of whether
                      you incur charges as a result.
                      <br />
                      <br />
                      You consent to receive the information, follow-up communications, and disclosures related to this application and any
                      subsequent credit transaction from Ford Credit affiliates, agents, and service providers, in writing, verbally or
                      electronically.
                      <br />
                      <br />
                      You have reviewed the Ford Credit{' '}
                      <a href='https://www.ford.com/finance/privacy' target='_blank' rel='noreferrer'>
                        privacy statement
                      </a>{' '}
                      and the privacy policy for the dealer you select.
                    </p>
                  )}
                </div>
              </div>
              <div className='accept-terms-checkbox-container prequal--web--checkbox-padding-fomo'>
                <label className='prequal--web--consent-text-fomo' htmlFor='checkbox-terms-consent'>
                  <Checkbox
                    id='checkbox-terms-and-consent'
                    type='checkbox'
                    name='checkbox-terms-consent'
                    validateScroll={!didScrollToBottomOfTerms}
                    dataTestId='checkbox-terms-consent'
                    checked={customerData.acceptedTermsAndConditions.value}
                    onChange={onAcceptTermsHandler}
                  />
                  <span role='presentation' onClick={onClick}>
                    I have read and agree to the above terms and conditions
                  </span>
                </label>
              </div>
              {!didScrollToBottomOfTerms && (
                <div className='prequal--web--accept-terms-checkbox-error-container-fomo'>
                  <img src={errorIcon} alt='warning' />
                  <p className='prequal--web--accept-terms-checkbox-error-text'>
                    Please scroll and read through the full Prequalification Terms & Conditions and check the box above before submitting
                    your application.
                  </p>
                </div>
              )}
              <>
                <div className='prequal--wc--bottom-header-container-fomo'>
                  <hr className='prequal-form-line' />
                  <h3 className='prequal--wc--review-bottom-header-fomo' data-testid='zero-credit-impact-text'>
                    Zero credit impact, no obligation.
                  </h3>
                </div>
              </>
            </div>
          </div>
        </div>
        <FooterNavigation props={props} customerData={customerData} />
      </div>
      <TimeOutSession appConfig={appConfig} dispatch={dispatch} customerData={customerData} navigate={navigate} />
    </>
  );
}

export default PrequalReview;
